import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  SxProps,
  Theme,
} from "@mui/material";

// Interface for select options
export interface SelectOption {
  label: string;
  value: string | number;
}

// Interface for validation rules
export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: string) => string | null; // Returns error message or null
}

// Main component props interface
export interface LabelValueDisplayProps {
  label: string | React.ReactElement;
  value: string | number | React.ReactElement;
  type?: "text" | "input" | "select";
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;

  // Styling props
  labelSx?: SxProps<Theme>;
  valueSx?: SxProps<Theme>;
  containerSx?: SxProps<Theme>;

  // Select specific props
  options?: SelectOption[];

  // Input specific props
  validation?: ValidationRule;
  inputType?: "text" | "number" | "email" | "password";

  // Event handlers
  onChange?: (value: string | number) => void;
  onBlur?: (value: string | number) => void;
  onFocus?: () => void;

  // Error handling
  showError?: boolean;
  errorMessage?: string;
}

/**
 * LabelValueDisplay Component
 *
 * A flexible component that can display a label with different types of values:
 * - text: Static text display
 * - input: Editable input field with validation
 * - select: Dropdown selection
 *
 * @param props - Component props
 */
function LabelValueDisplay({
  label,
  value,
  type = "text",
  placeholder,
  disabled = false,
  readOnly = false,
  labelSx = {},
  valueSx = {},
  containerSx = {},
  options = [],
  validation,
  inputType = "text",
  onChange,
  onBlur,
  onFocus,
  showError = false,
  errorMessage,
}: LabelValueDisplayProps) {
  const [internalError, setInternalError] = useState<string>("");
  const [isTouched, setIsTouched] = useState(false);

  // Default styling
  const defaultLabelSx: SxProps<Theme> = {
    backgroundColor: "var(--primary-500)",
    color: "var(--black)",
    padding: "9px 16px",
    textAlign: "center",
    fontWeight: "bold",
    fontSize: { xs: 16, md: 18, lg: 20 },
    borderRadius: "0",
    minHeight: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: type !== "text" ? "1px" : "0",
    ...labelSx,
  };

  const defaultValueSx: SxProps<Theme> = {
    backgroundColor: "#fff",
    padding: "13.5px 14px",
    textAlign: "center",
    fontSize: { xs: 16, md: 18, lg: 20 },
    borderRadius: "0",
    minHeight: "48px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...valueSx,
  };

  const defaultContainerSx: SxProps<Theme> = {
    display: "inline-block",
    minWidth: "120px",
    ...containerSx,
  };

  // Validation function
  const validateValue = useCallback(
    (val: string): string => {
      if (!validation) return "";

      const stringValue = String(val);

      if (validation.required && !stringValue.trim()) {
        return "この項目は必須です";
      }

      if (validation.minLength && stringValue.length < validation.minLength) {
        return `最小文字数は${validation.minLength}文字です`;
      }

      if (validation.maxLength && stringValue.length > validation.maxLength) {
        return `最大文字数は${validation.maxLength}文字です`;
      }

      if (validation.pattern && !validation.pattern.test(stringValue)) {
        return "形式が正しくありません";
      }

      if (validation.custom) {
        const customError = validation.custom(stringValue);
        if (customError) return customError;
      }

      return "";
    },
    [validation],
  );

  // Handle input change
  const handleInputChange = useCallback(
    (newValue: string | number) => {
      if (type === "input") {
        const error = validateValue(String(newValue));
        setInternalError(error);
      }

      onChange?.(newValue);
    },
    [onChange, type, validateValue],
  );

  // Handle blur event
  const handleBlur = useCallback(
    (newValue: string | number) => {
      setIsTouched(true);
      if (type === "input") {
        const error = validateValue(String(newValue));
        setInternalError(error);
      }
      onBlur?.(newValue);
    },
    [onBlur, type, validateValue],
  );

  // Determine if we should show error
  const shouldShowError = showError || (isTouched && internalError);
  const displayError = errorMessage || internalError;

  // Render value based on type
  const renderValue = () => {
    switch (type) {
      case "input":
        // Only allow string/number for input type
        if (typeof value !== "string" && typeof value !== "number") {
          return (
            <Box sx={{ ...defaultValueSx, minHeight: "30px", padding: "10px 8px" }}>
              <Typography
                sx={{ fontSize: "inherit", fontWeight: "inherit", color: "var(--red-800)" }}
              >
                Invalid value type for input
              </Typography>
            </Box>
          );
        }

        return (
          <TextField
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            InputProps={{
              readOnly,
            }}
            type={inputType}
            variant="outlined"
            size="small"
            fullWidth
            error={Boolean(shouldShowError && displayError)}
            // helperText={shouldShowError ? displayError : ""}
            onChange={(e) => handleInputChange(e.target.value)}
            onBlur={(e) => handleBlur(e.target.value)}
            onFocus={onFocus}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "0",
                backgroundColor: "#fff",
              },
              "& .MuiOutlinedInput-input": {
                textAlign: "center",
                padding: "13.5px 14px",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "1px solid var(--gray-250)",
              },
              ...valueSx,
            }}
          />
        );

      case "select":
        // Only allow string/number for select type
        if (typeof value !== "string" && typeof value !== "number") {
          return (
            <Box sx={{ ...defaultValueSx, minHeight: "30px", padding: "10px 8px" }}>
              <Typography
                sx={{ fontSize: "inherit", fontWeight: "inherit", color: "var(--red-800)" }}
              >
                Invalid value type for select
              </Typography>
            </Box>
          );
        }

        return (
          <FormControl fullWidth size="small">
            <Select
              value={value}
              disabled={disabled}
              onChange={(e) => handleInputChange(e.target.value)}
              onBlur={(e) => handleBlur(e.target.value as string)}
              onFocus={onFocus}
              sx={{
                borderRadius: "0",
                backgroundColor: "#fff",
                "& .MuiSelect-select": {
                  textAlign: "center",
                  padding: "13.5px 14px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid var(--gray-250)",
                },
                ...valueSx,
              }}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case "text":
      default:
        return (
          <Box sx={{ ...defaultValueSx, minHeight: "30px", padding: "10px 8px" }}>
            {typeof value === "string" || typeof value === "number" ? (
              <Typography sx={{ fontSize: "inherit", fontWeight: "inherit" }}>{value}</Typography>
            ) : (
              value
            )}
          </Box>
        );
    }
  };

  return (
    <Box sx={defaultContainerSx}>
      {/* Label */}
      <Box sx={defaultLabelSx}>
        {typeof label === "string" ? (
          <Typography sx={{ fontSize: "inherit", fontWeight: "inherit", color: "inherit" }}>
            {label}
          </Typography>
        ) : (
          label
        )}
      </Box>

      {/* Value */}
      {renderValue()}

      {/* Error message for all types */}
      {shouldShowError && displayError && (
        <Typography
          variant="caption"
          color="error"
          sx={{
            display: "block",
            mt: "2px",
            mb: "6px",
            color: "var(--red-800)",
            textAlign: "left",
            fontSize: { xs: 16, md: 18, xl: 20 },
          }}
        >
          {displayError}
        </Typography>
      )}
    </Box>
  );
}

export default LabelValueDisplay;
